import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer, Send, Loader2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * /brief — printable morning-brief page. Renders the exact same HTML the
 * scheduler emails out at 10:00 ET, so the in-app preview is guaranteed to
 * match what recipients see in their inbox (single source of truth on the
 * backend via `render_brief_html`).
 */
export default function BriefPage() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [copied, setCopied] = useState(false);
    const isAdmin = (user?.roles || []).includes("admin");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data: r } = await api.get("/brief/data");
                if (!cancelled) setData(r);
            } catch (e) {
                console.error("[Brief] load failed:", e?.message || e);
                if (!cancelled) toast.error("Failed to load brief.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const handleSend = async () => {
        setSending(true);
        try {
            const { data: r } = await api.post("/brief/send", {});
            toast.success(`Sent to ${r.recipients?.length || 0} recipient(s).`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Send failed.");
        } finally {
            setSending(false);
        }
    };

    const handleCopy = async () => {
        if (!data?.brief_text) return;
        try {
            await navigator.clipboard.writeText(data.brief_text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (e) {
            console.warn("[Brief] copy failed:", e?.message || e);
            toast.error("Copy failed.");
        }
    };

    const handlePrint = () => {
        // Print the embedded iframe (matches what the email looks like exactly).
        const iframe = document.getElementById("brief-frame");
        if (iframe?.contentWindow) {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
        } else {
            window.print();
        }
    };

    return (
        <div className="min-h-screen bg-virtus-bg text-white">
            {/* Toolbar */}
            <div className="sticky top-0 z-30 backdrop-blur-lg bg-black/70 border-b border-virtus-line">
                <div className="max-w-[900px] mx-auto px-3 md:px-6 py-3 flex items-center justify-between gap-2">
                    <Link
                        to="/hub"
                        className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim hover:text-virtus-orange"
                        data-testid="brief-back-hub"
                    >
                        <ArrowLeft size={12} />
                        <span className="hidden md:inline">HUB</span>
                    </Link>
                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            onClick={handleCopy}
                            disabled={loading}
                            className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim border border-virtus-line px-2 md:px-3 py-1.5 hover:text-virtus-orange hover:border-virtus-orange disabled:opacity-40"
                            data-testid="brief-copy-btn"
                            title="Copy the executive summary text"
                        >
                            {copied ? <Check size={11} /> : <Copy size={11} />}
                            <span className="hidden md:inline">{copied ? "COPIED" : "COPY SUMMARY"}</span>
                        </button>
                        <button
                            onClick={handlePrint}
                            disabled={loading}
                            className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim border border-virtus-line px-2 md:px-3 py-1.5 hover:text-virtus-orange hover:border-virtus-orange disabled:opacity-40"
                            data-testid="brief-print-btn"
                        >
                            <Printer size={11} />
                            <span className="hidden md:inline">PRINT</span>
                        </button>
                        {isAdmin && (
                            <button
                                onClick={handleSend}
                                disabled={sending || loading}
                                className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase bg-virtus-orange text-black px-2 md:px-3 py-1.5 hover:bg-white disabled:opacity-40 glow-orange"
                                data-testid="brief-send-now-btn"
                            >
                                {sending ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
                                <span className="hidden md:inline">{sending ? "SENDING…" : "SEND NOW"}</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="py-32 flex items-center justify-center text-virtus-dim">
                    <Loader2 className="animate-spin mr-2" /> Generating brief…
                </div>
            ) : data?.html ? (
                <iframe
                    id="brief-frame"
                    title="Morning Brief"
                    srcDoc={data.html}
                    className="w-full block bg-transparent"
                    style={{ height: "calc(100vh - 56px)", border: 0 }}
                    data-testid="brief-iframe"
                />
            ) : (
                <div className="py-32 text-center text-virtus-dim">No brief available.</div>
            )}
        </div>
    );
}
